import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosTextArea } from 'src/core/POS/PosTextArea';
import { formatCurrency } from 'src/utils/numberFormatter';
import { ExternalPaymentMethodType } from 'src/WebApiController';

import { RetailSaleFormValues } from '../types';
import * as styles from './ExternalPaymentSection.css';

/**
 * Maps each ExternalPaymentMethodType numeric value (as string key) to its
 * human-readable label. Used as `valueOptionsContent` for PosSelect.
 */
const EXTERNAL_PAYMENT_METHOD_LABELS: Record<string, string> = {
  [String(ExternalPaymentMethodType.CreditCard)]: 'Credit Card',
  [String(ExternalPaymentMethodType.Wire)]: 'Wire',
  [String(ExternalPaymentMethodType.ACH)]: 'ACH',
  [String(ExternalPaymentMethodType.Check)]: 'Check',
  [String(ExternalPaymentMethodType.CustomerCredit)]: 'Customer Credit',
  [String(ExternalPaymentMethodType.Cash)]: 'Cash',
  [String(ExternalPaymentMethodType.Other)]: 'Other',
};

/**
 * Returns the adaptive note placeholder based on the selected payment method.
 */
const getNotePlaceholder = (
  methodType: ExternalPaymentMethodType | null | undefined
): string => {
  switch (methodType) {
    case ExternalPaymentMethodType.CreditCard:
      return 'Card reference or last 4 digits';
    case ExternalPaymentMethodType.Wire:
      return 'Wire reference number';
    case ExternalPaymentMethodType.ACH:
      return 'ACH confirmation number';
    case ExternalPaymentMethodType.Check:
      return 'Check number';
    case ExternalPaymentMethodType.CustomerCredit:
      return 'Credit reference or notes';
    case ExternalPaymentMethodType.Cash:
      return 'Payment notes (optional)';
    case ExternalPaymentMethodType.Other:
      return 'Describe the payment method or reference';
    default:
      return 'Payment method or notes (optional)…';
  }
};

type ExternalPaymentSectionProps = {
  disabled: boolean;
  /**
   * The total sale amount (proceeds + line items). The external payment amount
   * is locked to this value in Step 1; partial payment support comes in Step 2.
   */
  totalAmount: number;
};

/**
 * Renders the sub-form shown when the broker selects "Paid Externally" in
 * the retail sale checkout step. Collects:
 * - Payment method type (required) — rendered as a dropdown
 * - Note (optional, max 500 chars) — placeholder adapts to selected method
 * - Amount (read-only, locked to sale total — partial payments coming in Step 2)
 * - Date incurred (defaults to today, cannot be future)
 */
export const ExternalPaymentSection = ({
  disabled,
  totalAmount,
}: ExternalPaymentSectionProps) => {
  const { watch, setValue, formState } = useFormContext<RetailSaleFormValues>();

  const methodType = watch('externalPaymentMethodType');
  const dateStr = watch('externalPaymentDate');
  const note = watch('externalPaymentNote');
  const currencyCode = watch('currencyCode') ?? 'USD';

  // Step 1: always keep externalPaymentAmount in sync with the sale total.
  // Partial payment support (where the broker enters a different amount) comes in Step 2.
  useEffect(() => {
    setValue('externalPaymentAmount', totalAmount, { shouldValidate: false });
  }, [totalAmount, setValue]);

  const notePlaceholder = getNotePlaceholder(methodType);

  const methodTypeError = formState.errors.externalPaymentMethodType?.message;
  const dateError = formState.errors.externalPaymentDate?.message;
  const noteError = formState.errors.externalPaymentNote?.message;

  // Hoist today's date so it is not recalculated on every render.
  const maxDate = useMemo(() => new Date(), []);

  // Convert the stored ISO date string to a Date object for DatePickerInput.
  const dateValue = useMemo(
    () => (dateStr ? new Date(dateStr) : undefined),
    [dateStr]
  );

  return (
    <div className={styles.sectionContainer}>
      {/* Payment method type dropdown */}
      <PosFormField label="Payment Method Type *" errors={methodTypeError}>
        <PosSelect
          value={methodType != null ? String(methodType) : undefined}
          placeholderText="Select payment method"
          valueOptionsContent={EXTERNAL_PAYMENT_METHOD_LABELS}
          sortMode="none"
          disabled={disabled}
          hasErrors={!!methodTypeError}
          showFullWidth
          onChange={(value) => {
            const parsed =
              value === ''
                ? null
                : (value as unknown as ExternalPaymentMethodType);
            setValue('externalPaymentMethodType', parsed, {
              shouldValidate: true,
            });
          }}
        />
      </PosFormField>

      {/* Note */}
      <PosFormField label="Note" errors={noteError}>
        <PosTextArea
          placeholder={notePlaceholder}
          maxLength={500}
          disabled={disabled}
          value={note ?? ''}
          onChange={(e) =>
            setValue('externalPaymentNote', e.target.value || null, {
              shouldValidate: true,
            })
          }
        />
      </PosFormField>

      {/* Amount — read-only in Step 1; locked to the sale total */}
      <PosFormField label="Amount">
        <div className={styles.amountRow}>
          <span className={styles.currencyLabel}>{currencyCode}</span>
          <span>{formatCurrency(totalAmount, currencyCode)}</span>
        </div>
        <span
          style={{
            fontSize: '0.75rem',
            color: 'var(--color-text-secondary, #666)',
          }}
        >
          Full payment only. Partial payment support coming soon.
        </span>
      </PosFormField>

      {/* Date incurred */}
      <PosFormField label="Date Incurred *" errors={dateError}>
        <DatePickerInput
          date={dateValue}
          maxDate={maxDate}
          disabled={disabled}
          onDateChange={(date: Date) => {
            setValue('externalPaymentDate', date.toISOString().split('T')[0], {
              shouldValidate: true,
            });
          }}
        />
      </PosFormField>
    </div>
  );
};
